import {
  CreateTicketNoteRequest,
  CreateTicketRequest,
  TicketDashboardRequestParams,
  TicketListResponse,
  TicketNotesResponse,
  TicketResponse,
  TicketSignedUploadURLResponse,
  UpdateTicketRequest,
} from "@telegraphio/bapi-types";
import apiClient, { handleApiError } from "../openApiClient";

export interface CustomerSupportApi {
  // --
  // Tickets
  // --
  getTickets(customerId: string, requestData: Partial<TicketDashboardRequestParams>): Promise<TicketListResponse>;
  getTicket(customerId: string, ticketId: string): Promise<TicketResponse>;
  createTicket(customerId: string, requestData: CreateTicketRequest): Promise<TicketResponse>;
  updateTicket(customerId: string, ticketId: string, requestData: UpdateTicketRequest): Promise<TicketResponse>;
  deleteTicket(customerId: string, ticketId: string): Promise<TicketResponse>;

  // --
  // Ticket Notes
  // --
  getTicketNotes(customerId: string, ticketId: string): Promise<TicketNotesResponse>;
  createTicketNote(
    customerId: string,
    ticketId: string,
    requestData: CreateTicketNoteRequest,
  ): Promise<TicketNotesResponse>;
  getTicketNoteSignedUploadURL(customerId: string): Promise<TicketSignedUploadURLResponse>;
}

export const customerSupportApi: CustomerSupportApi = {
  // --
  // Tickets
  // --
  async getTickets(customerId, requestData) {
    // Convert array fields to comma-separated strings for the API
    const query = Object.fromEntries(
      Object.entries(requestData).map(([key, value]) => [key, Array.isArray(value) ? value.join(",") : value]),
    );

    const { data, error, response } = await apiClient.GET("/support/{customer_id}", {
      params: {
        path: {
          customer_id: customerId,
        },
        query,
      },
    });

    if (error) {
      handleApiError(error, response, "Failed to fetch tickets");
    }

    return data;
  },

  async getTicket(customerId, ticketId) {
    const { data, error, response } = await apiClient.GET("/support/{customer_id}/{ticket_str}", {
      params: {
        path: {
          customer_id: customerId,
          ticket_str: ticketId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, "Failed to fetch ticket");
    }

    return data;
  },

  async createTicket(customerId, requestData) {
    const { data, error, response } = await apiClient.POST("/support/{customer_id}", {
      params: {
        path: {
          customer_id: customerId,
        },
      },
      body: requestData,
    });

    if (error) {
      handleApiError(error, response, "Failed to create ticket");
    }

    return data;
  },

  async updateTicket(customerId, ticketId, requestData) {
    const { data, error, response } = await apiClient.PATCH("/support/{customer_id}/{ticket_str}", {
      params: {
        path: {
          customer_id: customerId,
          ticket_str: ticketId,
        },
      },
      body: requestData,
    });

    if (error) {
      handleApiError(error, response, "Failed to update ticket");
    }

    return data;
  },

  async deleteTicket(customerId, ticketId) {
    const { data, error, response } = await apiClient.DELETE("/support/{customer_id}/{ticket_str}", {
      params: {
        path: {
          customer_id: customerId,
          ticket_str: ticketId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, "Failed to delete ticket");
    }

    return data;
  },

  // --
  // Ticket Notes
  // --
  async getTicketNotes(customerId, ticketId) {
    const { data, error, response } = await apiClient.GET("/support/{customer_id}/{ticket_str}/notes", {
      params: {
        path: {
          customer_id: customerId,
          ticket_str: ticketId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, "Failed to fetch ticket notes");
    }

    return data;
  },

  async createTicketNote(customerId, ticketId, requestData) {
    const { data, error, response } = await apiClient.POST("/support/{customer_id}/{ticket_str}/notes", {
      params: {
        path: {
          customer_id: customerId,
          ticket_str: ticketId,
        },
      },
      body: requestData,
    });

    if (error) {
      handleApiError(error, response, "Failed to create ticket note");
    }

    return data;
  },

  async getTicketNoteSignedUploadURL(customerId) {
    const { data, error, response } = await apiClient.GET("/support/{customer_id}/notes/upload", {
      params: {
        path: {
          customer_id: customerId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, "Failed to get ticket note signed upload URL");
    }

    return data;
  },
};
